/**
 * SELECT-USERS.MODAL
 * Select a list of users
 */
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { faUserCircle } from "@fortawesome/free-solid-svg-icons"
import { store } from "@/index"
import Modal from "@/components/modal"
import Checkbox from "@/components/checkbox"
import ListContainer from "@/components/list-container"
import ListItem from "@/components/list-item"
import ListIcon from "@/components/list-icon"
import ListCell from "@/components/list-cell"
import Space from "@/components/space"
import { userFetch, userFetchCount } from "@/redux/user.actions"
import { formatUsername, User } from "@/redux/user.types"
import { STATUS_LOADED, STATUS_SEARCHING } from "@/redux/_status.types"

interface Props {
  title: string
  selectedIds: string[]
  onClose: () => void
  onNext: (ids: string[]) => void
}

const LIMIT = 5

export default function SelectUsersModal(props: Props) {
  const { t } = useTranslation()
  const [selectedIds, setSelectedIds] = useState<string[]>(props.selectedIds)
  const [search, setSearch] = useState("")
  const [offset, setOffset] = useState(0)
  const [users, setUsers] = useState<User[]>([])
  const [count, setCount] = useState(0)
  const [isSearching, setIsSearching] = useState(true)

  //Every user of the account, searched and paginated by the server
  useEffect(() => {
    async function load() {
      setIsSearching(true)
      const response: any = await store.dispatch(userFetch(search, LIMIT, offset, [], ""))
      const total: any = await store.dispatch(userFetchCount(search, []))
      setUsers(response.error ? [] : response.map((user: Partial<User>) => new User(user)))
      setCount(total.error ? 0 : total.count)
      setIsSearching(false)
    }
    load()
  }, [search, offset])

  function toggle(id: string) {
    setSelectedIds(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id])
  }

  return (
    <Modal
      title={props.title}
      isLarge
      isCloseButtonVisible
      onClose={props.onClose}
      onNext={() => {
        props.onNext(selectedIds)
        props.onClose()
      }}>
      <div className="modal-content-list">
        <ListContainer
          countLimit={LIMIT}
          countSearch={count}
          isFooterActive
          onSearch={(value: string) => {
            setOffset(0)
            setSearch(value)
          }}
          onSearchStart={() => setIsSearching(true)}
          onSetPage={setOffset}
          status={isSearching ? STATUS_SEARCHING : STATUS_LOADED}
          extraActions={
            <span className="grey-t">
              {t("selected_users", { count: selectedIds.length, s: selectedIds.length > 1 ? "s" : "" })}
            </span>
          }>
          {users.map((user) => (
            <ListItem key={user.id} isEditable onClick={() => toggle(user.id)}>
              <ListCell width={48}>
                <Checkbox active={selectedIds.includes(user.id)} onClick={() => toggle(user.id)} />
              </ListCell>
              <ListIcon fallbackIcon={faUserCircle} image={user.imageUrl} />
              <ListCell width={220} text={formatUsername(user)} />
              <ListCell width={250}>
                <span className="grey-t">{user.email}</span>
              </ListCell>
              <Space />
            </ListItem>
          ))}
        </ListContainer>
      </div>
    </Modal>
  )
}
